import PubNub from 'pubnub'

import { pubNub as pubNubApi } from '../configuration'
import { User } from '../rest/types/User'
import { ImageTransformer } from '../relay/Transformers/ImageTransformer'
let instance: PubNub | null = null

export const getPubNubInstance = (userUUID: string): PubNub | null => {
  if (!instance || instance.getUUID() !== userUUID) {
    instance = new PubNub({
      publishKey: pubNubApi.PUBNUB_PUBLISH_KEY, // allow publishing of messages
      subscribeKey: pubNubApi.PUBNUB_SUBSCRIBE_KEY ?? '', // allow subscribing to channel
      uuid: userUUID, // unique for each client
      restore: true, // enable catchup on missed messages
      // authKey: 'authKey', //required if PAM is enabled on the keyset
    })
  }

  return instance
}

export const resetPubNubInstance = () => {
  instance = null
}

export const getUuid = (user: any) => {
  return `${user.id}_${user.firstName.replace(
    /\s/g,
    ''
  )}_${user.lastName.replace(/\s/g, '')}`
}

export const generateChannelID = (
  userId1: User['id'],
  userId2: User['id'],
  answerId: string | number
) => {
  const user1ID = parseInt(userId1, 10)
  const user2ID = parseInt(userId2, 10)

  return user1ID > user2ID
    ? `channel_${user2ID}_${answerId}_${user1ID}`
    : `channel_${user1ID}_${answerId}_${user2ID}`
}

export const getTimetoken = (pubnub: PubNub) => pubnub.time()

export const getMembershipToChannel = async (
  pubnub: PubNub,
  uuid: string
): Promise<PubNub.ManageMembershipsResponse<
  PubNub.ObjectCustom,
  PubNub.ObjectCustom
> | null> => {
  let memberships: any = null
  try {
    memberships = await pubnub.objects.getMemberships({
      uuid,
      include: { customFields: true },
    })
  } catch (error) {
    console.error(error)
  }

  return memberships
}

export const setMembershipToChannel = async (
  pubnub: any,
  uuid: any,
  channel: any
) => {
  const result = await getTimetoken(pubnub)

  const arg = {
    uuid,
    channels: [
      { id: channel, custom: { lastReadTimetoken: result.timetoken } },
    ],
  }

  try {
    await pubnub.objects.setMemberships(arg)
  } catch (error) {
    console.error(`An error occured with pubnub whil setting membership`, error)
  }

  return {
    channel: { id: channel },
    custom: { lastReadTimetoken: result.timetoken },
  }
}

export const hasAlreadyAMembershipToChannel = (
  memberships:
    | PubNub.ChannelMembershipObject<PubNub.ObjectCustom, PubNub.ObjectCustom>[]
    | null,
  channelID: string
): boolean => {
  if (!memberships) {
    return false
  }

  return memberships.some((membership) => membership.channel.id === channelID)
}

export const setUserMetadata = async (pubnub: PubNub, user: User) => {
  const avatar = ImageTransformer((user?.avatar as any) ?? null)

  const name = `${user.firstName} ${user.lastName}`
  const profileUrl = avatar?.src as string

  try {
    await pubnub.objects.setUUIDMetadata({
      data: profileUrl !== undefined ? { name, profileUrl } : { name },
    })
  } catch (error) {
    const _error = error as any
    const statusCode = _error?.status?.statusCode ?? _error?.status

    // PubNub may respond with 204 (No Content) while still succeeding.
    if (statusCode === 204) {
      return
    }

    console.error('Error occured on setUserMetadata', error)
  }
}

// Fetch only the channel members: getAllUUIDMetadata is capped at 100 users per page.
export const getChannelUsersMetadata = async (
  pubnub: PubNub,
  channel: string
) => {
  try {
    const { data } = await pubnub.objects.getChannelMembers({
      channel,
      include: { UUIDFields: true },
    })

    return data.map(
      (member) => member.uuid as PubNub.UUIDMetadataObject<PubNub.ObjectCustom>
    )
  } catch (error) {
    console.error('Error occured on getChannelUsersMetadata', error)
    return null
  }
}
